import { SecuredClient } from 'Core/Api/SecuredClient';
import axios, { CancelToken, CancelTokenSource } from 'axios';

export interface CustomDateParams {
  startDate: string;
  endDate: string;
  comparisonDays?: number;
}

export class DashboardHandler {
  async getDashboardData(cancelToken?: CancelToken) {
    const client = new SecuredClient();
    client.showLoading = false;
    client.handleGeneralError = true;
    try {
      return await client.getAdminDashboard(cancelToken);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  async getStatisticData(cancelToken?: CancelToken, customDate?: CustomDateParams) {
    const client = new SecuredClient();
    client.showLoading = false;
    client.handleGeneralError = true;
    try {
      if (customDate) {
        const params: Record<string, string> = {
          startDate: customDate.startDate,
          endDate: customDate.endDate,
        };
        if (customDate.comparisonDays !== undefined) {
          params.comparisonDays = customDate.comparisonDays.toString();
        }
        return await this.get(client, 'GetStatisticsForAdminDashboard', params, cancelToken);
      }
      return await client.getStatisticsForAdminDashboard(undefined, undefined, undefined, cancelToken);
    } catch (error) {
      console.error('Error fetching statistic data:', error);
      throw error;
    }
  }

  async getGameHistory(period: string = '30D', cancelToken?: CancelToken, customDate?: CustomDateParams) {
    const client = new SecuredClient();
    client.showLoading = false;
    client.handleGeneralError = true;
    try {
      if (customDate) {
        const params: Record<string, string> = {
          period: 'custom',
          startDate: customDate.startDate,
          endDate: customDate.endDate,
        };
        if (customDate.comparisonDays !== undefined) {
          params.comparisonDays = customDate.comparisonDays.toString();
        }
        return await this.get(client, 'GetGameHistory', params, cancelToken);
      }
      return await client.getGameHistory(period, undefined, undefined, undefined, cancelToken);
    } catch (error) {
      console.error('Error fetching game history:', error);
      throw error;
    }
  }

  async getTopPerformers(period: string, cancelToken?: CancelToken, customDate?: CustomDateParams) {
    const client = new SecuredClient();
    client.showLoading = false;
    client.handleGeneralError = true;
    try {
      const params: Record<string, string> = {
        period,
        cacheBust: Date.now().toString(),
      };

      if (customDate) {
        params.period = 'custom';
        params.startDate = customDate.startDate;
        params.endDate = customDate.endDate;
        if (customDate.comparisonDays !== undefined) {
          params.comparisonDays = customDate.comparisonDays.toString();
        }
      }

      return await this.get(client, 'GetTopPerformers', params, cancelToken);
    } catch (error) {
      console.error('Error fetching top performers:', error);
      throw error;
    }
  }

  async getTopPerformersByMode(mode: string, period: string, cancelToken?: CancelToken, customDate?: CustomDateParams) {
    const client = new SecuredClient();
    client.showLoading = false;
    client.handleGeneralError = true;
    try {
      const params: Record<string, string> = { mode, period, cacheBust: Date.now().toString() };
      if (customDate) {
        params.period = 'custom';
        params.startDate = customDate.startDate;
        params.endDate = customDate.endDate;
        if (customDate.comparisonDays !== undefined) {
          params.comparisonDays = customDate.comparisonDays.toString();
        }
      }
      return await this.get(client, 'GetTopPerformersByMode', params, cancelToken);
    } catch (error) {
      console.error('Error fetching top performers by mode:', error);
      throw error;
    }
  }

  async getActiveUsers(period: string, page: number = 1, pageSize: number = 20, cancelToken?: CancelToken, customDate?: CustomDateParams) {
    const client = new SecuredClient();
    client.showLoading = false;
    client.handleGeneralError = true;
    try {
      const params: Record<string, string> = { period, page: page.toString(), pageSize: pageSize.toString() };
      if (customDate) {
        params.period = 'custom';
        params.startDate = customDate.startDate;
        params.endDate = customDate.endDate;
      }
      return await this.get(client, 'GetActiveUsers', params, cancelToken);
    } catch (error) {
      console.error('Error fetching active users:', error);
      throw error;
    }
  }

  async getUserCount(cancelToken?: CancelToken) {
    const client = new SecuredClient();
    client.showLoading = false;
    client.handleGeneralError = true;
    try {
      return await client.getUserCount(cancelToken);
    } catch (error) {
      console.error('Error fetching user count:', error);
      throw error;
    }
  }

  async getRevenue(cancelToken?: CancelToken, customDate?: { startDate: string; endDate: string }) {
    const client = new SecuredClient();
    client.showLoading = false;
    client.handleGeneralError = true;
    try {
      const params: Record<string, string> = {};
      if (customDate) {
        params.startDate = customDate.startDate;
        params.endDate = customDate.endDate;
      }
      return await this.get(client, 'GetRevenue', params, cancelToken);
    } catch (error) {
      console.error('Error fetching revenue:', error);
      throw error;
    }
  }

  private get(client: SecuredClient, action: string, params?: Record<string, string>, cancelToken?: CancelToken): Promise<any> {
    const query = params
      ? '?' + Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
          .join('&')
      : '';
    const url = `${(client as any).baseUrl}/sa/sudokuvs/admin/commands/${action}${query}`;
    return client.axiosInstance.request({
      method: 'GET',
      url,
      headers: {
        'Accept': 'text/plain',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
      cancelToken,
    }).then(r => r.data);
  }
}

// Export singleton handler instance for convenience
export const dashboardHandler = new DashboardHandler();

// Cancel token utilities — use these instead of importing axios directly
export type { CancelTokenSource };
export const createCancelSource = () => axios.CancelToken.source();
export const isRequestCancelled = (err: unknown) => axios.isCancel(err);
